/**
 * Canonical HeirWise learning-analytics events.
 *
 * Follows the `src/lib/analytics/beta.ts` pattern: this module owns *naming*
 * and *safe property construction* only. Transport is the existing fan-out hub
 * `trackMetaEvent` (Meta custom event → GA4 mirror → PostHog mirror), which
 * already runs every payload through `sanitizeAnalyticsProps`.
 *
 * Privacy contract (enforced by construction, not by filtering):
 *   - Only the allow-listed product-metadata keys below are ever emitted.
 *   - No email, name, age, household/child data, auth data, financial values,
 *     free text, quiz answer text, or selected answers. We record that a UX
 *     step was reached/completed — never what a learner answered.
 *   - `undefined` / `null` properties are omitted rather than guessed.
 */

import { trackMetaEvent } from "@/lib/metaPixel";

export const LEARNING_EVENTS = [
  "lesson_started",
  "video_viewed",
  "infographic_viewed",
  "decide_started",
  "decide_completed",
  "quiz_started",
  "quiz_completed",
  "lesson_completed",
  "module_started",
  "module_completed",
] as const;

export type LearningEvent = (typeof LEARNING_EVENTS)[number];

/** The complete set of properties a learning event may ever carry. */
export interface LearningEventContext {
  lessonId?: string | null;
  lessonTitle?: string | null;
  lessonOrder?: number | null;
  courseId?: string | null;
  courseTitle?: string | null;
  moduleId?: string | null;
  moduleTitle?: string | null;
  discipline?: string | null;
  stage?: string | null;
  isBetaPath?: boolean | null;
  betaStepOrder?: number | null;
}

/** Snake_case wire names, in the documented order. */
export const LEARNING_PROPERTY_KEYS = [
  "lesson_id",
  "lesson_title",
  "lesson_order",
  "course_id",
  "course_title",
  "module_id",
  "module_title",
  "discipline",
  "stage",
  "is_beta_path",
  "beta_step_order",
] as const;

export type LearningPropertyKey = (typeof LEARNING_PROPERTY_KEYS)[number];

/**
 * Builds the wire payload from UI/state context. Unavailable values are
 * omitted; empty strings are treated as unavailable. Nothing outside
 * `LEARNING_PROPERTY_KEYS` can be produced.
 */
export function buildLearningProps(
  ctx: LearningEventContext = {},
): Partial<Record<LearningPropertyKey, string | number | boolean>> {
  const raw: Record<LearningPropertyKey, unknown> = {
    lesson_id: ctx.lessonId,
    lesson_title: ctx.lessonTitle,
    lesson_order: ctx.lessonOrder,
    course_id: ctx.courseId,
    course_title: ctx.courseTitle,
    module_id: ctx.moduleId,
    module_title: ctx.moduleTitle,
    discipline: ctx.discipline,
    stage: ctx.stage,
    is_beta_path: ctx.isBetaPath,
    beta_step_order: ctx.betaStepOrder,
  };

  const out: Partial<Record<LearningPropertyKey, string | number | boolean>> = {};
  for (const key of LEARNING_PROPERTY_KEYS) {
    const value = raw[key];
    if (value === undefined || value === null) continue;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) continue;
      out[key] = trimmed;
      continue;
    }
    if (typeof value === "number") {
      if (!Number.isFinite(value)) continue;
      out[key] = value;
      continue;
    }
    if (typeof value === "boolean") out[key] = value;
  }
  return out;
}

/**
 * Fire a learning event through the existing analytics fan-out.
 * Never throws — analytics must never break a lesson.
 */
export function trackLearningEvent(event: LearningEvent, ctx: LearningEventContext = {}): void {
  try {
    void trackMetaEvent(event, { customData: buildLearningProps(ctx) });
  } catch {
    /* noop */
  }
}

/* ---------------- Duplicate protection ---------------- */

/**
 * Process-lifetime registry of already-emitted `event|scope` pairs.
 *
 * React rerenders, route transitions, StrictMode double-invocation, remounts,
 * reopened modals and returning to a completed stage all resolve to the same
 * scope key and therefore emit at most once. A genuinely new attempt supplies
 * a new scope (e.g. a fresh attempt id), which legitimately re-emits.
 */
const firedKeys = new Set<string>();

function registryKey(event: LearningEvent, scope: string): string {
  return `${event}|${scope}`;
}

/** Returns true when the event was emitted, false when suppressed as duplicate. */
export function trackLearningEventOnce(
  event: LearningEvent,
  scope: string,
  ctx: LearningEventContext = {},
): boolean {
  const key = registryKey(event, scope);
  if (firedKeys.has(key)) return false;
  firedKeys.add(key);
  trackLearningEvent(event, ctx);
  return true;
}

export function hasFiredLearningEvent(event: LearningEvent, scope: string): boolean {
  return firedKeys.has(registryKey(event, scope));
}

/**
 * Clear duplicate-protection state. With no argument, clears everything
 * (tests). With a scope prefix, clears just that progression sequence — used
 * when a learner starts a genuinely new attempt of the same lesson.
 */
export function resetLearningDedupe(scopePrefix?: string): void {
  if (!scopePrefix) {
    firedKeys.clear();
    return;
  }
  for (const key of Array.from(firedKeys)) {
    const scope = key.slice(key.indexOf("|") + 1);
    if (scope.startsWith(scopePrefix)) firedKeys.delete(key);
  }
}
